import React, { useEffect, useRef, useState } from 'react';
import dropin from 'braintree-web-drop-in';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

// mode: 'subscription' | 'addon'
export const BraintreeCheckout = ({ open, onOpenChange, mode, planType, addonType, title, priceLabel, amount, onSuccess }) => {
  const containerRef = useRef(null);
  const instanceRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!open) return undefined;
    setReady(false);
    setError('');
    (async () => {
      try {
        const res = await axios.get(`${API}/payments/client-token`, authHeaders());
        if (cancelled || !containerRef.current) return;
        const config = {
          authorization: res.data.client_token,
          container: containerRef.current,
          dataCollector: true,
          card: { cardholderName: { required: false } },
        };
        // PayPal is opt-in: only offered once linked to the Braintree account.
        if (res.data.paypal_enabled) {
          config.paypal = mode === 'subscription'
            ? { flow: 'vault', billingAgreementDescription: title || 'Intentional Connections subscription' }
            : { flow: 'checkout', amount: amount || '0.00', currency: res.data.currency };
        }
        instanceRef.current = await dropin.create(config);
        if (!cancelled) setReady(true);
      } catch (e) {
        if (!cancelled) setError('Could not load the payment form. Please try again.');
      }
    })();
    return () => {
      cancelled = true;
      if (instanceRef.current) { try { instanceRef.current.teardown(); } catch (e) {} instanceRef.current = null; }
    };
  }, [open, mode, title, amount]);

  const submit = async () => {
    if (!instanceRef.current) return;
    setBusy(true); setError('');
    try {
      const payload = await instanceRef.current.requestPaymentMethod();
      const body = mode === 'subscription'
        ? { nonce: payload.nonce, device_data: payload.deviceData, plan_type: planType }
        : { nonce: payload.nonce, device_data: payload.deviceData, addon_type: addonType };
      const endpoint = mode === 'subscription' ? `${API}/payments/subscribe` : `${API}/payments/addons`;
      const res = await axios.post(endpoint, body, authHeaders());
      toast.success(mode === 'subscription' ? 'Subscription activated! 🎉' : 'Purchase complete! 🎉');
      onSuccess && onSuccess(res.data);
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Payment was declined. Please try another method.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="braintree-checkout-dialog">
        <DialogHeader>
          <DialogTitle>{title || 'Complete your payment'}</DialogTitle>
          <DialogDescription>
            {priceLabel ? `${priceLabel} · ` : ''}Pay securely with card or PayPal. You can cancel anytime.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-[220px]">
          {!ready && !error && (
            <div className="flex items-center justify-center py-10 text-gray-500" data-testid="bt-loading">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading secure payment form…
            </div>
          )}
          <div ref={containerRef} data-testid="bt-dropin-container" />
          {error && <p className="text-sm text-red-600 mt-2" data-testid="bt-error">{error}</p>}
        </div>
        <Button
          data-testid="bt-pay-btn"
          onClick={submit}
          disabled={!ready || busy}
          className="w-full bg-rose-600 hover:bg-rose-700"
        >
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</>
                : <><Lock className="w-4 h-4 mr-2" /> {mode === 'subscription' ? 'Start Subscription' : 'Pay Now'}</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default BraintreeCheckout;
